import { RadioChannel, RadioRegion } from "@somewear/api";
import {
	ButtonFunction,
	InmarsatLeaseType,
	RadioBandwidth,
	RadioMode,
	RadioSpreadFactor,
} from "@somewear/api/src/proto/command_public_proto_pb";
import {
	ButtonFunctionIds,
	enableableField,
	InmarsatLeaseTypeIds,
	MeshKeyMethodVersion,
	optionalBoolSchema,
	protoToZodEnum,
	RadioModeIds,
	TrackingIntervalIds,
} from "@somewear/model";
import { z } from "zod";

// The defaults of the workspace default settings.
export const DEFAULT_WORKSPACE_SETTINGS_VALUES = {
	trackingIntervals: {
		mesh: TrackingIntervalIds.ATAK30,
		sat: TrackingIntervalIds.ATAK2Min,
	},
	lowBandwidthTrackingMultiplier: 1,
	radioChannel: null,
	backhaul: true,
	altitude: false,
	speedAndCourse: false,
	trackingOn: true,
	enableLed: true,
	enableHaptics: true,
	enableSatellite: true,
	enableMesh: true,
	buttonFunction: ButtonFunctionIds.TRACKING,
	radioMode: RadioModeIds.ENABLED,
	meshKeyMethodVersion: MeshKeyMethodVersion.UNKNOWN,
	inmarsatFrequencies: {
		rx: 1518,
		tx: 1626.5,
	},
	inmarsatLeaseType: InmarsatLeaseType.INMARSATLEASETYPELAISR,
};

export function getDefaultRadioChannel(radioChannels: RadioChannel.AsObject[]) {
	const initialRadioChannel = radioChannels.find(
		(rc) => rc.region === RadioRegion.RADIOREGIONUS && rc.name === "Americas 1",
	);

	if (!initialRadioChannel) return;
	return {
		enabled: true,
		value: initialRadioChannel.id,
	};
}

export const workspaceDefaultsFieldsSchema = z.object({
	trackingOn: enableableField(z.boolean().default(DEFAULT_WORKSPACE_SETTINGS_VALUES.trackingOn)),
	backhaul: enableableField(z.boolean().default(DEFAULT_WORKSPACE_SETTINGS_VALUES.backhaul)),
	enableLed: enableableField(z.boolean().default(DEFAULT_WORKSPACE_SETTINGS_VALUES.enableLed)),
	enableHaptics: enableableField(
		z.boolean().default(DEFAULT_WORKSPACE_SETTINGS_VALUES.enableHaptics),
	),
	enableSatellite: enableableField(
		z.boolean().default(DEFAULT_WORKSPACE_SETTINGS_VALUES.enableSatellite),
	),
	buttonFunction: enableableField(
		z.nativeEnum(ButtonFunctionIds).default(DEFAULT_WORKSPACE_SETTINGS_VALUES.buttonFunction),
	),
	altitude: enableableField(z.boolean().default(DEFAULT_WORKSPACE_SETTINGS_VALUES.altitude)),
	speedAndCourse: enableableField(
		z.boolean().default(DEFAULT_WORKSPACE_SETTINGS_VALUES.speedAndCourse),
	),
	trackingIntervals: enableableField(
		z
			.union([
				z.object({
					mesh: z.nativeEnum(TrackingIntervalIds),
					sat: z.nativeEnum(TrackingIntervalIds),
				}),
				z.nativeEnum(TrackingIntervalIds),
			])
			.default(DEFAULT_WORKSPACE_SETTINGS_VALUES.trackingIntervals),
	),
	radioChannel: enableableField(z.number().nullable().default(null)),
	radioMode: enableableField(
		z.nativeEnum(RadioModeIds).default(DEFAULT_WORKSPACE_SETTINGS_VALUES.radioMode),
	),
	meshKeyMethodVersion: enableableField(
		z
			.nativeEnum(MeshKeyMethodVersion)
			.default(DEFAULT_WORKSPACE_SETTINGS_VALUES.meshKeyMethodVersion),
	),
	inmarsatFrequencies: enableableField(
		z
			.object({
				rx: z.number(),
				tx: z.number(),
			})
			.default(DEFAULT_WORKSPACE_SETTINGS_VALUES.inmarsatFrequencies),
	),
	inmarsatLeaseType: enableableField(
		z
			.nativeEnum(InmarsatLeaseTypeIds)
			.default(DEFAULT_WORKSPACE_SETTINGS_VALUES.inmarsatLeaseType),
	),
});

export const workspaceDefaultsSchema = workspaceDefaultsFieldsSchema.extend({
	workspaceId: z.string(),
});

export const baseWorkspaceDeviceDefaultsRequestSchema = z.object({
	// Optional Bools
	trackingOn: optionalBoolSchema,
	backhaul: optionalBoolSchema,
	altitude: optionalBoolSchema,
	speedAndCourse: optionalBoolSchema,
	ledDisabled: optionalBoolSchema,
	hapticsDisabled: optionalBoolSchema,
	satDisabled: optionalBoolSchema,
	// Tracking Interval = trackingInterval + gpsInterval
	trackingInterval: z.number(),
	gpsInterval: z.number(),
	lowBandwidthTrackingMultiplier: z.number(),
	// Radio Channel = lowSpeedFrequency + highSpeedFrequency
	lowSpeedFrequency: z.number(),
	highSpeedFrequency: z.number(),
	// Radio Data Rate = bandwidth + spreadFactor
	lowSpeedBandwidth: protoToZodEnum(RadioBandwidth),
	lowSpeedSpreadFactor: protoToZodEnum(RadioSpreadFactor),

	highSpeedBandwidth: protoToZodEnum(RadioBandwidth).nullable(),
	highSpeedSpreadFactor: protoToZodEnum(RadioSpreadFactor).nullable(),

	radioMode: protoToZodEnum(RadioMode), // Maps to "Enable Mesh"
	buttonFunction: protoToZodEnum(ButtonFunction).optional(),
	meshKeyMethodVersion: optionalBoolSchema,
	inmarsatRxFrequencyHz: z.number(),
	inmarsatTxFrequencyHz: z.number(),
	inmarsatLeaseType: protoToZodEnum(InmarsatLeaseType),
});

export const workspaceDefaultsRequestSchema = baseWorkspaceDeviceDefaultsRequestSchema.extend({
	workspaceId: z.string(),
});

export type BaseWorkspaceDefaultsRequestSchema = z.infer<
	typeof baseWorkspaceDeviceDefaultsRequestSchema
>;
export type WorkspaceDefaultsSchema = z.infer<typeof workspaceDefaultsSchema>;
export type WorkspaceDefaultsFieldsSchema = z.infer<typeof workspaceDefaultsFieldsSchema>;
export type WorkspaceDefaultsRequestSchema = z.infer<typeof workspaceDefaultsRequestSchema>;

export function isWorkspaceDeviceDefaultsField(
	key: string,
): key is keyof WorkspaceDefaultsFieldsSchema {
	return key in workspaceDefaultsFieldsSchema.shape;
}
